import clsx from 'clsx'
import type { PropsWithChildren } from 'react'
import React from 'react'

interface BaseProps {
  /**
   * If true, the divider is positioned absolutely at the bottom-left of its container
   * @default false
   */
  absolute?: boolean
  /**
   * The component used for the root node
   * @default 'hr'
   */
  component?: DividerComponent
}

type OrientationProps =
  | {
      /**
       * The orientation of the divider
       * @default 'horizontal'
       */
      orientation?: 'horizontal'
      /**
       * If true and orientation is vertical, the divider will stretch to fill the height of its container
       * @default false
       */
      flex?: never
    }
  | {
      /**
       * The orientation of the divider
       * @default 'horizontal'
       */
      orientation: 'vertical'
      /**
       * If true and orientation is vertical, the divider will stretch to fill the height of its container
       * @default false
       */
      flex?: boolean
    }

type VariantProps =
  | {
      /**
       * The size of the inset when variant is 'inset'
       * @default 'small'
       */
      insetSize?: DividerInsetSize
      /**
       * Visual style variant of the divider
       * @default 'fullWidth'
       */
      variant: 'inset'
    }
  | {
      /**
       * The size of the inset when variant is 'inset'
       * @default 'small'
       */
      insetSize?: never
      /**
       * Visual style variant of the divider
       * @default 'fullWidth'
       */
      variant?: DividerVariant
    }

export type DividerProps = BaseProps & VariantProps & OrientationProps

type DividerComponent = 'hr' | 'li'

type DividerInsetSize = 'small' | 'medium' | 'large'

type DividerVariant = 'fullWidth' | 'inset' | 'middle'

const variants = {
  fullWidth: '',
  inset: {
    small: 'ml-4',
    medium: 'ml-15',
    large: 'ml-18',
  },
  middle: 'mx-4',
}

function Divider({
  absolute = false,
  component: Component = 'hr',
  flex = false,
  insetSize = 'small',
  orientation = 'horizontal',
  variant = 'fullWidth',
}: PropsWithChildren<DividerProps>): JSX.Element {
  const width = orientation === 'vertical' ? 'w-px' : absolute ? 'w-full' : null
  const height =
    orientation === 'vertical'
      ? flex && !absolute
        ? 'self-stretch h-auto'
        : 'h-full'
      : 'h-px'

  return (
    <Component
      aria-hidden="true"
      className={clsx(
        'shrink-0 m-0 border-none bg-black-10',
        absolute ? 'absolute bottom-0 left-0' : null,
        variant === 'inset' ? variants.inset[insetSize] : variants[variant],
        width,
        height,
      )}
      role="separator"
    />
  )
}

export default Divider
